import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import useBoardStore from '../../../store/boardStore';

type SaveTemplateDialogProps = {
  open: boolean;
  onClose: () => void;
  onSaveTemplate: (input: { name: string; description: string }) => Promise<boolean>;
};

const SaveTemplateDialog = ({
  open,
  onClose,
  onSaveTemplate,
}: SaveTemplateDialogProps) => {
  const actionError = useBoardStore((state) => state.actionError);
  const clearActionError = useBoardStore((state) => state.clearActionError);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setName('');
    setDescription('');
    setIsSaving(false);
    clearActionError();
  }, [clearActionError, open]);

  const sanitizedName = name.trim();
  const canSave = sanitizedName.length > 0 && !isSaving;

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    setIsSaving(true);

    try {
      const didSave = await onSaveTemplate({
        name: sanitizedName,
        description: description.trim(),
      });

      if (didSave) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !isSaving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Save as Template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {actionError ? <Alert severity="error">{actionError}</Alert> : null}
          <TextField
            label="Template name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={isSaving}
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={isSaving}
            fullWidth
            multiline
            minRows={4}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {isSaving ? 'Saving...' : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveTemplateDialog;
